import type React from 'react';
import { styled, css, useTheme } from 'styled-components';

import BarsLoader from '../../Elements/BarsLoader/index.tsx';

export interface IItem extends React.PropsWithChildren {
  wrap?: boolean;
  disabled?: boolean;
  isLoading?: boolean;
  onClick?: (e: React.MouseEvent<HTMLElement>) => void;
  id?: string;
  externalLink?: string;
  isCustomChildren?: boolean;
}

const ExternalLink = styled.a`
  display: flex;
  align-items: center;
  padding: 5px 15px;
  width: 100%;
  min-height: 36px;
  color: ${({ theme }) => theme.colors.black_1}!important;
  text-decoration: none;
`;

const Wrapper = styled.li<{
  $externalLink: string;
  $wrap?: boolean;
  $isCustomChildren?: boolean;
  $disabled?: boolean;
}>`
  padding: ${({ $externalLink }) => ($externalLink ? 0 : '5px 15px')};
  font-size: 13px;
  display: flex;
  justify-content: space-between;
  min-height: 36px;
  align-items: center;
  transition: 0.15s;
  white-space: ${({ $wrap }) => ($wrap ? 'normal' : 'nowrap')};
  color: ${({ theme }) => theme.colors.black_1};
  position: ${({ $isCustomChildren }) => $isCustomChildren && 'relative'};
  > *:not(:first-child) {
    margin-left: 10px;
  }
  ${({ $disabled }) =>
    $disabled &&
    css`
      opacity: 0.3;
      cursor: not-allowed;
    `}
  ${({ $disabled }) =>
    !$disabled &&
    css`
      cursor: pointer;
      &:hover {
        background-color: ${({ theme }) => theme.colors.grey05_1};
      }
    `}
`;

const Item: React.FC<IItem> = ({
  children,
  isLoading = false,
  wrap,
  disabled = false,
  onClick = undefined,
  id,
  externalLink = '',
  isCustomChildren,
  ...other
}) => {
  const theme = useTheme();
  return (
    <Wrapper
      $wrap={wrap}
      $disabled={disabled || isLoading}
      onClick={(e: React.MouseEvent<HTMLElement>) => !isLoading && !disabled && onClick && onClick(e)}
      id={id}
      $externalLink={externalLink}
      $isCustomChildren={isCustomChildren}
      {...other}>
      {externalLink ? (
        <ExternalLink href={externalLink} target='_blank' rel='noopener noreferrer'>
          {children}
        </ExternalLink>
      ) : (
        <>{isCustomChildren ? children : <span>{children}</span>}</>
      )}
      {isLoading && <BarsLoader bgColor={theme.colors.black_1} />}
    </Wrapper>
  );
};

export default Item;
